
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { SUBSCRIPTION_TIERS, getTierById } from '@/config/subscriptionTiers';
import { UserSubscription, SubscriptionTier } from '@/types/subscription';
import { supabase } from '@/integrations/supabase/client';

export const useEnhancedSubscription = () => {
  const { user, isAuthenticated, getToken, hasRole } = useAuth();
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState({
    calculationsUsed: 0,
    reportsGenerated: 0,
    propertiesTracked: 0
  });

  const checkSubscription = useCallback(async () => {
    console.log('useEnhancedSubscription: Starting subscription check');
    
    if (!isAuthenticated || !user?.email) {
      console.log('useEnhancedSubscription: User not authenticated');
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      // Give admins enterprise-level access automatically
      if (hasRole(['admin', 'owner'])) {
        console.log('useEnhancedSubscription: Admin user detected, granting enterprise access');
        setSubscription({
          tier: 'enterprise',
          status: 'active',
          currentPeriodEnd: null,
          features: getTierById('enterprise')?.features || []
        });
        setLoading(false);
        return;
      }

      const token = await getToken();
      if (!token) {
        console.warn('useEnhancedSubscription: No auth token available');
        throw new Error('No auth token available');
      }

      console.log('useEnhancedSubscription: Checking database for subscription');

      // First check the new subscriptions table
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (subscriptionData && !subscriptionError) {
        console.log('useEnhancedSubscription: Found active subscription:', subscriptionData);
        const tier = getTierById(subscriptionData.tier);
        setSubscription({
          tier: subscriptionData.tier as UserSubscription['tier'],
          status: subscriptionData.status as any,
          currentPeriodEnd: subscriptionData.current_period_end ? new Date(subscriptionData.current_period_end) : null,
          stripeSubscriptionId: subscriptionData.stripe_subscription_id,
          features: tier?.features || [],
        });
        setLoading(false);
        return;
      }

      // Fallback to legacy subscribers table
      const { data: legacyData, error: legacyError } = await supabase
        .from('subscribers')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (legacyData && !legacyError) {
        console.log('useEnhancedSubscription: Found legacy subscription:', legacyData);
        // Map legacy plan names to new tier system
        const tierMapping: Record<string, string> = {
          'free': 'freemium',
          'pro': 'pro',
          'investor': 'investor',
          'agent': 'agent_pro',
          'enterprise': 'enterprise',
        };

        const tier = tierMapping[legacyData.plan] || 'freemium';
        const tierConfig = getTierById(tier);

        setSubscription({
          tier: tier as UserSubscription['tier'],
          status: legacyData.status === 'active' ? 'active' : 'inactive',
          currentPeriodEnd: null,
          stripeSubscriptionId: legacyData.stripe_subscription_id,
          features: tierConfig?.features || [],
        });
      } else {
        console.log('useEnhancedSubscription: No subscription found, using freemium');
        // No subscription found, default to freemium
        const freemiumTier = getTierById('freemium');
        setSubscription({
          tier: 'freemium',
          status: 'active',
          currentPeriodEnd: null,
          features: freemiumTier?.features || [],
        });
      }
    } catch (error) {
      console.error('useEnhancedSubscription: Failed to check subscription:', error);
      // Fallback to freemium on error for regular users
      setSubscription({
        tier: 'freemium',
        status: 'inactive',
        currentPeriodEnd: null,
        features: getTierById('freemium')?.features || []
      });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.email, getToken, hasRole]);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  const hasFeature = useCallback((feature: string): boolean => {
    // Admins have all features
    if (hasRole(['admin', 'owner'])) {
      console.log('useEnhancedSubscription: Admin has all features:', feature);
      return true;
    }

    if (!subscription) return false;
    return subscription.features.includes(feature);
  }, [subscription, hasRole]);

  const canPerformAction = useCallback((action: 'calculate' | 'report' | 'track'): boolean => {
    // Admins have unlimited actions
    if (hasRole(['admin', 'owner'])) {
      return true;
    }

    if (!subscription) return false;
    
    const tier = getTierById(subscription.tier);
    if (!tier?.limitations) return true;

    switch (action) {
      case 'calculate':
        return !tier.limitations.calculationsPerMonth || 
               usage.calculationsUsed < tier.limitations.calculationsPerMonth;
      case 'report':
        return !tier.limitations.reportsPerMonth || 
               usage.reportsGenerated < tier.limitations.reportsPerMonth;
      case 'track':
        return !tier.limitations.propertiesTracked || 
               usage.propertiesTracked < tier.limitations.propertiesTracked;
      default:
        return true;
    }
  }, [subscription, usage, hasRole]);

  const getUpgradePrompt = useCallback((blockedFeature: string) => {
    const currentTier = subscription?.tier;
    
    if (currentTier === 'freemium') {
      if (blockedFeature.includes('report') || blockedFeature.includes('download')) {
        return { suggestedTier: 'pro', reason: 'PDF reports and downloads' };
      }
      return { suggestedTier: 'pro', reason: 'unlimited calculations' };
    }
    
    if (currentTier === 'pro') {
      if (blockedFeature.includes('investment') || blockedFeature.includes('rental')) {
        return { suggestedTier: 'investor', reason: 'investment analysis tools' };
      }
    }
    
    if (currentTier === 'investor') {
      if (blockedFeature.includes('brand') || blockedFeature.includes('client')) {
        return { suggestedTier: 'agent_pro', reason: 'professional client tools' };
      }
    }

    return { suggestedTier: 'enterprise', reason: 'advanced features' };
  }, [subscription]);

  const createCheckoutSession = async (tierId: string) => {
    const tier = getTierById(tierId);
    if (!tier || tier.id === 'enterprise') {
      // Handle enterprise contact sales
      window.open('mailto:sales@equiforge.com?subject=Enterprise Inquiry', '_blank');
      return;
    }

    try {
      // Enhanced debugging and validation
      console.log('=== CHECKOUT SESSION DEBUG START ===');
      console.log('User authenticated:', isAuthenticated);
      console.log('User object:', user);
      console.log('Tier:', tier);
      
      if (!isAuthenticated || !user) {
        throw new Error('User must be authenticated to create checkout session');
      }

      const token = await getToken();
      console.log('Token retrieved, length:', token?.length || 0);
      console.log('Token exists:', !!token);
      
      if (!token) {
        throw new Error('No auth token available - please sign in again');
      }

      if (!tier.stripeId || tier.stripeId === '') {
        throw new Error('No Stripe price ID configured for this tier');
      }

      const requestBody = { priceId: tier.stripeId };
      console.log('Request body to be sent:', JSON.stringify(requestBody));
      console.log('Authorization header will include token:', token.substring(0, 20) + '...');

      // Use fetch directly to ensure body is properly sent
      const response = await fetch(
        `https://njoxbqtqffsfhkwgbksu.supabase.co/functions/v1/create-checkout-session`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        }
      );

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Checkout error response:', errorText);
        throw new Error(`Failed to create checkout session: ${errorText}`);
      }

      const data = await response.json();
      console.log('Checkout session response data:', data);

      if (!data?.url) {
        throw new Error('No checkout URL returned');
      }

      console.log('Checkout session created successfully, redirecting to:', data.url);
      
      // Redirect to Stripe checkout, handling iframe context
      try {
        if (window.parent && window.parent !== window.self) {
          // Running in iframe, try to communicate with parent window
          console.log('Detected iframe context, using PostMessage API');
          window.parent.postMessage({
            type: 'LOVABLE_OPEN_URL',
            url: data.url
          }, '*');
          
          // Wait briefly for parent to handle message
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // If still here, parent didn't handle it, open in new tab
          const newWindow = window.open(data.url, '_blank');
          if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            throw new Error('Please allow popups to complete checkout');
          }
        } else {
          // Not in iframe, normal redirect
          window.location.href = data.url;
        }
      } catch (e) {
        console.error('Redirect failed:', e);
        throw new Error('Unable to open checkout. Please allow popups and try again.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      // Re-throw the error so it can be handled by the calling component
      throw error;
    }
  };

  const manageBilling = async () => {
    try {
      const token = await getToken();
      if (!token) {
        throw new Error('No auth token available');
      }

      console.log('manageBilling: Creating portal session');

      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (error) {
        console.error('Portal error:', error);
        const errorMessage = error.details || error.message || 'Failed to create portal session';
        throw new Error(errorMessage);
      }

      if (!data?.url) {
        throw new Error('No portal URL returned');
      }

      console.log('manageBilling: Opening portal in new window');
      // Open billing portal in a new tab
      window.open(data.url, '_blank');
    } catch (error) {
      console.error('Portal error:', error);
      throw error; // Re-throw to allow caller to handle
    }
  };

  // Debug logging removed to prevent infinite console loop
  // Only log on subscription changes, not every render

  return {
    subscription,
    loading,
    usage,
    hasFeature,
    canPerformAction,
    getUpgradePrompt,
    createCheckoutSession,
    manageBilling,
    refreshSubscription: checkSubscription,
    availableTiers: SUBSCRIPTION_TIERS
  };
};
